import * as React from "react";
import {
  Calendar,
  DateHeaderProps,
  EventProps,
  momentLocalizer,
  Views,
} from "react-big-calendar";
import moment from "moment";
import "moment/locale/ja";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Holiday } from "@/models/billingPaymentSchedule";
import {
  formatYearMonthJA,
  getDateStrFromDate,
  splitYearMonth,
} from "@/util/datetime";
import { BillingPaymentScheduleConstants } from "./BillingPaymentScheduleConstants";
import {
  EVENT_COLORS,
  OverflowNotice,
  ScheduleCalendarEvent,
} from "./BillingPaymentScheduleEvents";

moment.locale("ja");
const localizer = momentLocalizer(moment);

// イベントのホバー時に正式名称を表示する。react-big-calendar標準のtooltipAccessor
// （要素のtitle属性）はブラウザ制御のため表示まで時間がかかる。MUIのTooltipに
// 差し替え、enterDelayを短くすることで素早く表示されるようにする
// （標準のtitle属性はCalendarのtooltipAccessor={null}で無効化し、二重表示を防ぐ）
const EventContent: React.FC<EventProps<ScheduleCalendarEvent>> = ({ event }) => (
  <Tooltip title={event.fullTitle} enterDelay={150} enterNextDelay={150} arrow>
    <span>{event.title}</span>
  </Tooltip>
);

// 日付欄の数字の色（日曜日・祝日は赤、土曜日は青。祝日が土曜日と重なる場合は赤を優先する）
const SUNDAY_AND_HOLIDAY_COLOR = "#d32f2f";
const SATURDAY_COLOR = "#1976d2";

// 月切り替え・表示切り替えはUI上行わないため、no-opを渡す
const noop = () => {
  /* no-op */
};

const eventPropGetter = (event: ScheduleCalendarEvent) => {
  const colors = EVENT_COLORS[event.type];
  return {
    style: {
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: "0.7rem",
    },
  };
};

export type BillingPaymentScheduleMonthCardProps = {
  yearMonth: string; // yyyyMM
  published: boolean;
  events: ScheduleCalendarEvent[];
  holidays: Holiday[];
  // 翌月が未公開のため翌月のカードに表示できない繰り越しイベントの注釈（カレンダー外・カレンダー下部に表示）
  overflowNotices?: OverflowNotice[];
};

export const BillingPaymentScheduleMonthCard: React.FC<
  BillingPaymentScheduleMonthCardProps
> = ({ yearMonth, published, events, holidays, overflowNotices = [] }) => {
  const { year, month } = splitYearMonth(yearMonth);
  const calendarDate = new Date(year, month - 1, 1);

  const holidayDates = React.useMemo(
    () => new Set(holidays.map((h) => h.date)),
    [holidays]
  );

  // 日付欄の数字だけに色をつける（枠全体は着色しない）。祝日・日曜日は赤、土曜日は青とし、
  // 祝日が土曜日と重なる場合は赤を優先する
  const DateNumber: React.FC<DateHeaderProps> = React.useCallback(
    ({ date, label }) => {
      const dateStr = getDateStrFromDate(date);
      const dayOfWeek = date.getDay();
      const color =
        holidayDates.has(dateStr) || dayOfWeek === 0
          ? SUNDAY_AND_HOLIDAY_COLOR
          : dayOfWeek === 6
          ? SATURDAY_COLOR
          : undefined;
      return <span style={color ? { color } : undefined}>{label}</span>;
    },
    [holidayDates]
  );

  const calendarComponents = React.useMemo(
    () => ({
      toolbar: () => null,
      event: EventContent,
      month: { dateHeader: DateNumber },
    }),
    [DateNumber]
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {formatYearMonthJA(yearMonth)}
      </Typography>
      {!published ? (
        <Typography color="text.secondary">
          {BillingPaymentScheduleConstants.Preparing}
        </Typography>
      ) : (
        // 他画面向けの共通カレンダーCSS（.rbc-calendar { height: auto !important; }）が
        // コード分割なしでアプリ全体に読み込まれ、月表示の高さ指定を打ち消してしまうため、
        // ここでスコープを絞って上書きする（共通CSS自体は変更しない）
        <>
          <Box sx={{ "& .rbc-calendar": { height: "480px !important" } }}>
            <Calendar
              localizer={localizer}
              date={calendarDate}
              view={Views.MONTH}
              views={[Views.MONTH]}
              onNavigate={noop}
              onView={noop}
              components={calendarComponents}
              events={events}
              eventPropGetter={eventPropGetter}
              tooltipAccessor={null}
              style={{ height: 480 }}
            />
          </Box>
          {overflowNotices.length > 0 && (
            <Box
              sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              {overflowNotices.map((notice) => (
                <Box
                  key={notice.type}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: "2px",
                      fontSize: "0.7rem",
                      lineHeight: 1.4,
                      whiteSpace: "nowrap",
                      backgroundColor: EVENT_COLORS[notice.type].background,
                      color: EVENT_COLORS[notice.type].text,
                    }}
                  >
                    {notice.abbr}
                  </Box>
                  <Typography variant="body2">{notice.dateLabel}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};
